import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { ArrowRight, Check } from "lucide-react";

const OPTIONS = [
  {
    id: "marketing",
    title: "I work in marketing",
    subtitle: "I'm responsible for marketing strategy, activity or performance.",
  },
  {
    id: "leadership",
    title: "I lead the business",
    subtitle: "Marketing reports to me or forms part of my broader business responsibilities.",
  },
];

export default function PathwaySelect() {
  const navigate = useNavigate();
  const { update } = useDiagnostic();
  const [selected, setSelected] = useState(null);

  const handleContinue = () => {
    if (!selected) return;
    update({ pathway: selected });
    navigate("/about");
  };

  return (
    <div className="min-h-screen bg-white">
      <TopBar step="Step 1 of 4" />
      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="tw-fade-up">
          <p className="tw-eyebrow">Pathway</p>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            First, tell us where you sit.
          </h1>
          <p className="mt-4 max-w-xl text-lg text-neutral-600">
            The two pathways ask different questions but map to the same diagnostic framework.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
          {OPTIONS.map((o) => {
            const active = selected === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setSelected(o.id)}
                className={`group relative flex flex-col items-start rounded-sm border p-8 text-left transition-colors duration-200 ${
                  active
                    ? "border-2 border-[#1408F7] bg-[var(--tw-blue-tint)]"
                    : "border border-neutral-200 hover:border-neutral-400"
                }`}
                data-testid={`pathway-select-${o.id}-button`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                    active ? "border-[#1408F7] bg-[#1408F7] text-white" : "border-neutral-300"
                  }`}
                >
                  {active && <Check className="h-3.5 w-3.5" />}
                </span>
                <h2 className="mt-8 text-xl font-semibold tracking-tight text-neutral-900">
                  {o.title}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-neutral-600">{o.subtitle}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-12 flex justify-end">
          <button
            onClick={handleContinue}
            disabled={!selected}
            className="tw-btn-primary tw-focus inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-30"
            data-testid="pathway-continue-button"
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
